import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Loader2, KeyRound, ShieldAlert } from "lucide-react";

export default function Security() {
    const [logs, setLogs] = useState([]);
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [eventType, setEventType] = useState("");

    const [currentPwd, setCurrentPwd] = useState("");
    const [newPwd, setNewPwd] = useState("");
    const [pwdLoading, setPwdLoading] = useState(false);

    const fetchAll = async () => {
        setLoading(true);
        try {
            const [logsRes, sessRes] = await Promise.all([
                api.get("/admin/security-logs", { params: eventType ? { event_type: eventType } : {} }),
                api.get("/admin/sessions"),
            ]);
            setLogs(logsRes.data.items || []);
            setSessions(sessRes.data.items || []);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventType]);

    const handleChangePwd = async (e) => {
        e.preventDefault();
        if (newPwd.length < 6) {
            toast.error("Yeni şifre en az 6 karakter olmalı");
            return;
        }
        setPwdLoading(true);
        try {
            await api.put("/auth/change-password", {
                current_password: currentPwd,
                new_password: newPwd,
            });
            toast.success("Şifre güncellendi");
            setCurrentPwd("");
            setNewPwd("");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setPwdLoading(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="admin-security-page">
            <h1 className="text-2xl font-bold text-dark-navy">Güvenlik</h1>

            <div className="grid lg:grid-cols-2 gap-6">
                <div className="bg-white border border-slate-100 rounded-lg shadow-md p-5">
                    <div className="flex items-center justify-between mb-3">
                        <h2 className="text-sm font-semibold uppercase tracking-wider text-deep-blue inline-flex items-center gap-2">
                            <ShieldAlert className="w-4 h-4" /> Login Logları
                        </h2>
                        <select
                            value={eventType}
                            onChange={(e) => setEventType(e.target.value)}
                            className="text-xs rounded-md border border-slate-300 px-2 py-1.5"
                            data-testid="security-filter-event"
                        >
                            <option value="">Tümü</option>
                            <option value="login_success">Başarılı</option>
                            <option value="login_failed">Başarısız</option>
                            <option value="session_killed">Oturum sonlandırma</option>
                        </select>
                    </div>
                    {loading ? (
                        <div className="text-sm text-slate-500">Yükleniyor...</div>
                    ) : logs.length === 0 ? (
                        <div className="text-sm text-slate-500 py-4">Kayıt yok.</div>
                    ) : (
                        <div className="divide-y divide-slate-100" data-testid="security-logs-list">
                            {logs.map((l, i) => (
                                <div key={i} className="py-2.5 flex items-start justify-between gap-4 text-sm">
                                    <div>
                                        <div className={`font-semibold ${l.event_type === "login_failed" ? "text-data-red" : "text-dark-navy"}`}>
                                            {l.event_type}
                                        </div>
                                        <div className="text-xs text-slate-500">{l.ip_address || "-"}</div>
                                    </div>
                                    <div className="text-xs text-slate-500 font-data shrink-0">
                                        {new Date(l.created_at).toLocaleString("tr-TR")}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="bg-white border border-slate-100 rounded-lg shadow-md p-5">
                    <h2 className="text-sm font-semibold uppercase tracking-wider text-deep-blue mb-3">
                        Aktif Oturum Geçmişi
                    </h2>
                    {sessions.length === 0 ? (
                        <div className="text-sm text-slate-500 py-4">Oturum kaydı yok.</div>
                    ) : (
                        <div className="divide-y divide-slate-100" data-testid="sessions-list">
                            {sessions.map((s, i) => (
                                <div key={i} className="py-2.5 flex items-center justify-between text-sm">
                                    <div>
                                        <div className="font-semibold text-dark-navy">{s.details?.username || "-"}</div>
                                        <div className="text-xs text-slate-500">{s.ip_address || "-"}</div>
                                    </div>
                                    <div className="text-xs text-slate-500 font-data">{new Date(s.created_at).toLocaleString("tr-TR")}</div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <div className="bg-white border border-slate-100 rounded-lg shadow-md p-5 max-w-lg">
                <h2 className="text-sm font-semibold uppercase tracking-wider text-deep-blue mb-3 inline-flex items-center gap-2">
                    <KeyRound className="w-4 h-4" /> Şifre Değiştir
                </h2>
                <form className="space-y-3" onSubmit={handleChangePwd} data-testid="change-password-form">
                    <input
                        type="password"
                        placeholder="Mevcut şifre"
                        value={currentPwd}
                        onChange={(e) => setCurrentPwd(e.target.value)}
                        className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-teal"
                        required
                        data-testid="change-password-current"
                    />
                    <input
                        type="password"
                        placeholder="Yeni şifre (en az 6 karakter)"
                        value={newPwd}
                        onChange={(e) => setNewPwd(e.target.value)}
                        className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-teal"
                        required
                        data-testid="change-password-new"
                    />
                    <button
                        type="submit"
                        disabled={pwdLoading}
                        className="bg-dark-navy hover:bg-deep-blue text-white text-sm font-semibold px-4 py-2 rounded-md inline-flex items-center gap-2 disabled:opacity-70"
                        data-testid="change-password-submit"
                    >
                        {pwdLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                        Güncelle
                    </button>
                </form>
            </div>
        </div>
    );
}
